import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  Lock,
  ShieldCheck,
} from "lucide-react";
import Nav from "../site/Nav";
import Footer from "../site/Footer";
import DeliverySnapshot from "../admin/DeliverySnapshot";
import DeliveryChat from "../admin/DeliveryChat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "tme_admin_token";

const PACKAGE_FIELD_LABELS = {
  full_name: "Contact",
  business_name: "Business",
  email: "Email",
  phone: "Phone",
  service_type: "Service Type",
  revenue_range: "Revenue Range",
  monthly_leads: "Monthly Leads",
  tool_stack: "Tool Stack",
  current_tools: "Tools",
  biggest_leak: "Pain Point",
  workflow_bottleneck: "Workflow Bottleneck",
  ideal_start_date: "Ideal Start",
  notes: "Notes",
};

export default function AdminDeliveryWorkspacePage() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const token =
    typeof window !== "undefined"
      ? window.sessionStorage.getItem(TOKEN_KEY) || ""
      : "";

  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);

  const scrollRef = useRef(null);

  useEffect(() => {
    if (!token) {
      navigate("/admin/deliveries");
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/admin/deliveries/${workspaceId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setWorkspace(res.data);
      } catch (err) {
        if (!cancelled) {
          const status = err?.response?.status;
          if (status === 401 || status === 403) {
            window.sessionStorage.removeItem(TOKEN_KEY);
            navigate("/admin/deliveries");
            return;
          }
          setError(
            err?.response?.data?.detail || "Could not load workspace.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId, token, navigate]);

  // Auto-scroll on new messages.
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [workspace?.messages?.length, sending]);

  const send = async (e) => {
    e?.preventDefault();
    const msg = draft.trim();
    if (!msg || sending) return;
    setSending(true);
    setError(null);

    // Optimistic insert of the user message; reconcile after server reply.
    const optimisticUser = {
      role: "user",
      content: msg,
      ts: new Date().toISOString(),
    };
    setWorkspace((w) =>
      w
        ? { ...w, messages: [...(w.messages || []), optimisticUser] }
        : w,
    );
    setDraft("");

    try {
      const res = await axios.post(
        `${API}/admin/deliveries/${workspaceId}/messages`,
        { message: msg },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Replace optimistic + append assistant.
      setWorkspace((w) => {
        if (!w) return w;
        const trimmed = (w.messages || []).slice(0, -1);
        return {
          ...w,
          messages: [...trimmed, ...(res.data.messages || [])],
        };
      });
    } catch (err) {
      // Roll back optimistic on error.
      setWorkspace((w) => {
        if (!w) return w;
        return { ...w, messages: (w.messages || []).slice(0, -1) };
      });
      setError(
        err?.response?.data?.detail ||
          "The Delivery Agent could not respond. Try again.",
      );
      setDraft(msg);
    } finally {
      setSending(false);
    }
  };

  const intakeFields = useMemo(() => {
    const snap = workspace?.intake_snapshot || {};
    return Object.entries(PACKAGE_FIELD_LABELS)
      .map(([key, label]) => ({ key, label, value: snap[key] }))
      .filter((f) => f.value);
  }, [workspace]);

  if (!token) return null;

  if (loading) {
    return (
      <div className="bg-sand-100 text-ink min-h-screen">
        <Nav />
        <main className="pt-32 pb-20 px-5 sm:px-6 md:px-10">
          <div className="max-w-3xl mx-auto flex items-center gap-3 text-ink-muted">
            <Loader2 size={16} className="animate-spin" /> Loading workspace…
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!workspace) {
    return (
      <div className="bg-sand-100 text-ink min-h-screen">
        <Nav />
        <main className="pt-28 pb-20 px-5 sm:px-6 md:px-10">
          <div className="max-w-md mx-auto rounded-3xl border border-sand-300 bg-sand-50 p-8">
            <Lock size={20} className="text-clay" />
            <h1 className="mt-4 font-display text-2xl font-semibold text-ink">
              Workspace not found
            </h1>
            <p className="mt-3 text-[13.5px] text-ink-muted">
              {error ||
                "This delivery workspace is missing or you don't have access."}
            </p>
            <Link
              to="/admin/deliveries"
              className="mt-6 inline-flex items-center gap-2 text-clay text-[13px] hover:text-rust"
            >
              <ArrowLeft size={14} /> Back to deliveries
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div
      className="bg-sand-100 text-ink min-h-screen"
      data-testid="admin-delivery-workspace"
    >
      <Nav />
      <main className="pt-24 sm:pt-28 md:pt-32 pb-12 px-5 sm:px-6 md:px-10">
        <div className="max-w-7xl mx-auto">
          <Link
            to="/admin/deliveries"
            className="inline-flex items-center gap-2 text-[12.5px] text-ink-muted hover:text-clay transition-colors"
          >
            <ArrowLeft size={13} /> All deliveries
          </Link>

          <div className="mt-4 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay flex items-center gap-2">
                <ShieldCheck size={12} /> Admin · Delivery Workspace
              </p>
              <h1 className="font-display mt-3 text-[1.75rem] sm:text-[2.2rem] md:text-[2.6rem] leading-[1.05] tracking-[-0.02em] font-semibold text-ink">
                {workspace.business_name || "Untitled workspace"}
              </h1>
              <p className="mt-2 text-[13.5px] text-ink-muted">
                {workspace.package_label || workspace.package_selected || "—"}
                {" · "}
                <span className="font-mono text-[11px]">{workspace.current_stage || "—"}</span>
              </p>
            </div>
          </div>

          {/* Two-column workspace layout */}
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
            <DeliverySnapshot workspace={workspace} intakeFields={intakeFields} />
            <DeliveryChat
              workspace={workspace}
              sending={sending}
              error={error}
              draft={draft}
              setDraft={setDraft}
              onSend={send}
              scrollRef={scrollRef}
            />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
